import { z } from "zod";

export const runStatus = z.enum(["queued", "running", "completed", "failed", "cancelled"]);
export type RunStatus = z.infer<typeof runStatus>;

/**
 * Something the agent consulted, only where it succeeded:
 *
 * - a file its script read, such as `docs/search.md`, or with `pattern`, a grep
 *   under the prefix `path`. The UI shows neither: a file says nothing to the
 *   reader. Conversations from before search hold reads of `docs/goz/`;
 * - a search, as `path` `/api/search` with the query as `pattern`;
 * - a passage or document read from search, as `path`
 *   `/api/search/documents/{id}` with the document's `title`, which the path
 *   cannot supply.
 */
export const agentResource = z.strictObject({
  path: z.string(),
  pattern: z.string().optional(),
  title: z.string().optional(),
});
export type AgentResource = z.infer<typeof agentResource>;

/**
 * The wire format for the SSE stream. Defined once here, written by the
 * runner and parsed at the browser boundary — an unparsed `EventSource`
 * message is just as untyped as an unparsed fetch response.
 */
export const agentStreamEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("status"), status: runStatus }),
  z.object({ type: z.literal("thinking"), text: z.string() }),
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({ type: z.literal("error"), message: z.string() }),
  /**
   * One script the agent ran. Emitted before it runs (`ok` absent) and again
   * once it returns, so the UI can show a step in flight rather than only
   * completed work.
   *
   * `thought` is the model's stated reason, carried over from the Anna
   * reference project's fetch tool: a step trace that shows only "ran a
   * script" makes a run auditable in principle and unreadable in practice.
   */
  z.object({
    type: z.literal("tool"),
    id: z.string(),
    thought: z.string(),
    ok: z.boolean().optional(),
    summary: z.string().optional(),
    /** Set on the completion event, when the call looked something up. */
    resources: z.array(agentResource).optional(),
  }),
  z.object({
    type: z.literal("done"),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  }),
]);
export type AgentStreamEvent = z.infer<typeof agentStreamEvent>;
